import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";

const Section = ({ title, children }) => (
  <div className="space-y-1.5">
    {title && <p className="text-xs uppercase tracking-[0.12em] font-bold text-gold">{title}</p>}
    <div className="text-sm text-navy-deep/90 leading-relaxed space-y-2">{children}</div>
  </div>
);

export default function PrivacyDialog() {
  const { user, setUser } = useAuth();
  const [hide, setHide] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  const open = !!user && user.role === "socio" && !dismissed && (
    (user.privacy_version_accepted || 0) !== (user.privacy_current_version || 1) || !user.privacy_hide
  );

  const accept = async () => {
    setSubmitting(true);
    try {
      const { data } = await api.post("/auth/accept-privacy", { hide });
      setUser((u) => ({
        ...u,
        privacy_hide: data.privacy_hide,
        privacy_accepted_at: data.privacy_accepted_at,
        privacy_version_accepted: data.privacy_version_accepted,
        privacy_current_version: data.privacy_current_version,
      }));
      setDismissed(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open}>
      <DialogContent
        className="max-w-2xl p-0 gap-0 [&>button]:hidden"
        onInteractOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
        data-testid="privacy-dialog"
      >
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-navy/10">
          <DialogTitle className="font-display text-2xl text-navy">Directory Soci — Informativa e regole di utilizzo</DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">Leggi e accetta le regole di utilizzo per accedere alla directory dei soci.</DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[55vh] px-6 py-5">
          <div className="space-y-5">
            <Section>
              <p>La Directory Soci del Kiwanis Distretto Italia San Marino è uno strumento riservato esclusivamente ai Soci Kiwaniani, finalizzato a facilitare le comunicazioni, le relazioni associative e lo svolgimento delle attività proprie del Kiwanis.</p>
              <p>Accedendo alla Directory, il Socio può consultare i dati degli altri Soci resi disponibili all'interno dell'App, quali, a titolo esemplificativo, nome e cognome, Club di appartenenza, incarichi, indirizzo e-mail, numero di telefono e altri dati eventualmente pubblicati dal Socio secondo le modalità previste dal Distretto.</p>
            </Section>

            <Section title="Finalità dell'utilizzo">
              <p>I dati presenti nella Directory possono essere consultati e utilizzati esclusivamente per finalità connesse alla vita associativa e alle attività del Kiwanis, quali:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>comunicazioni tra Soci;</li>
                <li>organizzazione e partecipazione ad attività, riunioni, eventi e iniziative Kiwaniane;</li>
                <li>collaborazione nell'ambito dei Club, delle Divisioni e del Distretto;</li>
                <li>coordinamento di service, progetti e attività istituzionali;</li>
                <li>rapporti e comunicazioni inerenti agli incarichi associativi.</li>
              </ul>
            </Section>

            <Section title="Divieto di utilizzo commerciale">
              <p>È espressamente vietato utilizzare, raccogliere, copiare, estrarre, trasferire, diffondere o elaborare i dati presenti nella Directory:</p>
              <ul className="list-disc pl-5 space-y-1">
                <li>per attività di promozione commerciale o pubblicitaria;</li>
                <li>per attività di marketing diretto;</li>
                <li>per attività di vendita o proposta di prodotti e servizi;</li>
                <li>per attività professionali o imprenditoriali non riconducibili alle finalità del Kiwanis;</li>
                <li>per finalità di lucro;</li>
                <li>per campagne di comunicazione, mailing list o iniziative promozionali personali o di terzi;</li>
                <li>per cedere, comunicare o rendere disponibili i dati a soggetti estranei al Kiwanis;</li>
                <li>per qualsiasi altra finalità diversa da quelle associative sopra indicate.</li>
              </ul>
              <p>È inoltre vietato effettuare copie massive, esportazioni, scraping, acquisizioni automatizzate o qualsiasi altra forma di raccolta sistematica dei dati contenuti nella Directory.</p>
              <p>I dati presenti nella Directory devono essere considerati riservati e utilizzati nel rispetto della normativa vigente in materia di protezione dei dati personali e, in particolare, del Regolamento (UE) 2016/679 (GDPR).</p>
            </Section>

            <Section title="Responsabilità del Socio">
              <p>Ogni Socio è personalmente responsabile dell'utilizzo dei dati consultati attraverso la Directory e si impegna a non utilizzarli per finalità diverse da quelle associative.</p>
              <p>L'eventuale utilizzo improprio dei dati, la loro diffusione non autorizzata o il loro utilizzo per finalità commerciali, promozionali o lucrative potranno comportare la revoca dell'accesso alla Directory e all'App, fatti salvi gli ulteriori provvedimenti e le responsabilità previste dalla legge e dai regolamenti associativi.</p>
            </Section>

            <Section title="Protezione dei dati personali">
              <p>Il trattamento dei dati personali dei Soci avviene nel rispetto della normativa vigente in materia di protezione dei dati personali e delle informative privacy adottate dal Kiwanis Distretto Italia San Marino.</p>
              <p>Il Socio può esercitare i diritti previsti dal GDPR, compresi il diritto di accesso, rettifica, cancellazione, limitazione del trattamento e gli altri diritti riconosciuti dalla normativa, secondo le modalità indicate nell'informativa privacy del Distretto.</p>
              <p>Per informazioni o richieste relative al trattamento dei dati personali è possibile rivolgersi al Titolare del trattamento attraverso i recapiti ufficiali del Kiwanis Distretto Italia San Marino.</p>
            </Section>

            <Section title="Dichiarazione del Socio">
              <p>Dichiaro di aver letto e compreso le presenti regole e mi impegno a utilizzare la Directory esclusivamente per finalità connesse alle attività del Kiwanis.</p>
              <p>Accetto di non utilizzare, copiare, diffondere o elaborare i dati degli altri Soci per finalità commerciali, promozionali, pubblicitarie o lucrative e di non comunicarli a soggetti estranei al Kiwanis.</p>
            </Section>
          </div>
        </ScrollArea>

        <div className="px-6 py-4 border-t border-navy/10 space-y-4">
          <label className="flex items-center gap-2.5 cursor-pointer select-none" data-testid="privacy-hide-label">
            <Checkbox checked={hide} onCheckedChange={(v) => setHide(!!v)} data-testid="privacy-hide-checkbox" />
            <span className="text-sm text-navy-deep">Non mostrare più questo messaggio</span>
          </label>
          <Button onClick={accept} disabled={submitting} className="w-full h-11 bg-navy hover:bg-navy-hover text-white font-semibold" data-testid="privacy-accept-btn">
            {submitting ? "Attendere…" : "Accetto ed accedo alla directory"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
